import * as React from "react";
import { ScrollView } from "react-native";

import useFetchClazz from "../hooks/clazzes/useFetchClazz";
import { Clazz as ClazzType, User as UserType, Syllabus } from "../types";
import Layout from "../components/common/Layout";
import Text from "../components/common/Text";
import ToggleInput from "../components/common/ToggleInput";
import AppContext from "../app-context/AppContext";
import List from "../components/common/List";
import User from "../components/user";
import useListClazzRoster from "../hooks/clazzes/useListClazzRoster";
import Button from "../components/common/PressableButton";
import {
  Divider,
  Info,
  Space,
  JustifyContent,
  AlignItems,
} from "../styled-component";
import useListClazzSyllabi from "../hooks/clazzes/useListClazzSyllabi";
import CreateOrUpdateSyllabus from "../components/syllabus/CreateOrUpdateSyllabus";
import Card from "../components/common/Card";
import IconButton from "../common/components/IconButton";
import CreateOrUpdateClazz from "../components/clazz/CreateOrUpdateClazz";
import { Theme } from "../styles";

type Option = "syllabi" | "roster";
const options: Option[] = ["syllabi", "roster"];

export interface ClazzCardProps {
  clazzId?: ClazzType["id"];
  onPressSyllabus?: (syllbus: Syllabus) => void;
  onPressLearner?: (leaner: UserType) => void;
  greedy?: boolean;
  isSyllabusSelected?: (syllbus: Syllabus) => boolean;
  isLearnerSelected?: (leaner: UserType) => boolean;
}

export interface ClazzCardElement {
  setSelectedOption: (option: Option) => void;
}

const ClazzCard = React.forwardRef<ClazzCardElement, ClazzCardProps>(
  (
    {
      clazzId,
      onPressSyllabus,
      greedy,
      onPressLearner,
      isSyllabusSelected,
      isLearnerSelected,
    },
    ref
  ) => {
    const { data: clazz } = useFetchClazz(clazzId);
    const rosterQuery = useListClazzRoster(clazzId);
    const syllabiQuery = useListClazzSyllabi(clazzId);
    const [selectedOption, setSelectedOption] = React.useState<Option>(
      options[0]
    );
    const isRoster = selectedOption === "roster";
    const isSyllabi = selectedOption === "syllabi";

    React.useEffect(() => {
      const element = {
        setSelectedOption,
      };

      if (ref && "current" in ref) {
        ref.current = element;
      } else if (typeof ref === "function") {
        ref(element);
      }
    });

    return (
      <Card
        greedy={greedy}
        header={
          <>
            <Card.Header
              title={clazz?.name}
              label="Class"
              right={
                <AppContext.Instructor>
                  <CreateOrUpdateClazz id={clazzId}>
                    {({ onPress, title }) => (
                      <Button
                        title={title}
                        onPress={onPress}
                        // mode="contained"
                        color="accent"
                      />
                    )}

                    {/* {({ onPress }) => <IconButton name="edit" onPress={onPress} color="accent" />} */}
                  </CreateOrUpdateClazz>
                </AppContext.Instructor>
              }
            />
          </>
        }
      >
        <Layout.Column greedy>
          <Card.SubHeader
            left={
              <ToggleInput
                value={selectedOption}
                options={options}
                onChange={setSelectedOption}
              />
            }
            right={
              <AppContext.Instructor>
                {isSyllabi && clazzId && (
                  <Layout.Row>
                    <CreateOrUpdateSyllabus clazzId={clazzId}>
                      {({ onPress, title }) => (
                        <Button
                          title={title}
                          onPress={onPress}
                          mode="contained"
                        />
                      )}

                      {/* {({ onPress }) => (
                      <IconButton
                        name="plus"
                        background={Theme.colors.transparent}
                        onPress={onPress}
                      />
                    )} */}
                    </CreateOrUpdateSyllabus>

                    <Space />
                  </Layout.Row>
                )}
              </AppContext.Instructor>
            }
          />

          {isSyllabi && (
            <>
              <ScrollView>
                <Space />

                <List.Async<Syllabus>
                  {...syllabiQuery}
                  renderEmpty={() => (
                    <Layout.Box fullWidth alignItems="center" size={1}>
                      <AppContext.Instructor>
                        <Text textAlign="center">
                          Press 'Create Syllabus' to add a syllabus
                        </Text>
                      </AppContext.Instructor>

                      <AppContext.Learner>
                        <Text textAlign="center">No syllabi</Text>
                      </AppContext.Learner>
                    </Layout.Box>
                  )}
                  renderItem={({
                    item,
                    index,
                  }: {
                    item: Syllabus;
                    index: number;
                  }) => (
                    <>
                      <Button
                        onPress={() => onPressSyllabus?.(item)}
                        selected={isSyllabusSelected?.(item)}
                        roundness={0}
                      >
                        <List.Item
                          title={<Text.SubHeader>{item.name}</Text.SubHeader>}
                          description={
                            <Layout.Row alignItems="baseline">
                              <Text.Label>
                                Number of Moves: {item.moves?.length || 0}
                              </Text.Label>
                            </Layout.Row>
                          }
                        />
                      </Button>

                      {syllabiQuery.data &&
                        index < syllabiQuery.data.length - 1 && <Divider />}
                    </>
                  )}
                />

                {!!syllabiQuery.data?.length && <Space />}
              </ScrollView>
            </>
          )}

          {isRoster && (
            <>
              <ScrollView>
                <Space />

                <Button>
                  <List.Item
                    title={
                      <Text.SubHeader>
                        <User.DisplayName user={clazz?.instructor} />
                      </Text.SubHeader>
                    }
                    description={<Text.Label>Instructor</Text.Label>}
                  />
                </Button>

                <Divider />

                <List.Async<UserType>
                  {...rosterQuery}
                  // renderEmpty={() => (
                  //   <Layout.Box fullWidth alignItems="center">
                  //     <Text textAlign="center">No class members</Text>
                  //   </Layout.Box>
                  // )}
                  renderItem={({
                    item,
                    index,
                  }: {
                    item: UserType;
                    index: number;
                  }) => (
                    <>
                      <Button
                        onPress={() => onPressLearner?.(item)}
                        selected={isLearnerSelected?.(item)}
                        roundness={0}
                      >
                        <List.Item
                          title={
                            <Text.SubHeader>
                              <User.DisplayName user={item} />
                            </Text.SubHeader>
                          }
                          description={<Text.Label>Learner</Text.Label>}
                        />
                      </Button>

                      {rosterQuery.data &&
                        index < rosterQuery.data.length - 1 && <Divider />}
                    </>
                  )}
                />

                {!!rosterQuery.data?.length && <Space />}
              </ScrollView>
            </>
          )}
        </Layout.Column>
      </Card>
    );
  }
);

export default ClazzCard;
